/**
 * Thin wrapper over Google Identity Services.
 *
 * GIS is loaded from Google's CDN at runtime rather than bundled — Google
 * requires it to be served from their origin, and it is the one external script
 * this app loads.
 */

interface CredentialResponse {
  /** The ID token. Sent straight to the API, which is what verifies it. */
  credential: string;
}

interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_prompt?: boolean;
  }): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'small' | 'medium' | 'large';
      text?: 'signin_with' | 'signup_with' | 'continue_with';
      shape?: 'rectangular' | 'pill';
      width?: number;
      logo_alignment?: 'left' | 'center';
    },
  ): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let loader: Promise<GoogleAccountsId> | null = null;

/**
 * Loads the GIS script once and resolves with its API.
 *
 * The promise is cached so React's StrictMode double-mount, and any later
 * re-render, reuse the same load instead of injecting the script twice.
 */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  if (loader) return loader;

  loader = new Promise<GoogleAccountsId>((resolve, reject) => {
    if (window.google?.accounts.id) {
      resolve(window.google.accounts.id);
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SRC}"]`,
    );

    const onLoad = () => {
      if (window.google?.accounts.id) resolve(window.google.accounts.id);
      else reject(new Error('Google Identity Services loaded but exposed no API.'));
    };

    if (existing) {
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Identity Services.')),
      );
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', onLoad);
    script.addEventListener('error', () =>
      reject(new Error('Failed to load Google Identity Services.')),
    );
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    // Do not cache a failure: a transient network problem should not disable
    // sign-in for the rest of the page's life.
    loader = null;
    throw error;
  });

  return loader;
}

/** Client id from the build. Absent means SSO is not configured here. */
export function googleClientId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_CLIENT_ID || undefined;
}
